import type { RfpProgress } from "../../types/rfp";
import type { RfpSidebarSectionView } from "../../utils/rfpQuestionFilters";

type RfpQuestionAnswerSectionSidebarProps = {
  readonly sections: readonly RfpSidebarSectionView[];
  readonly activeSectionName: string | null;
  readonly progress: RfpProgress;
  readonly progressScopeLabel?: string;
  readonly onSectionChange: (sectionName: string) => void;
};

const clampPercent = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
};

export const RfpQuestionAnswerSectionSidebar = ({
  sections,
  activeSectionName,
  progress,
  progressScopeLabel = "answered",
  onSectionChange,
}: RfpQuestionAnswerSectionSidebarProps) => {
  const completed = Number.isFinite(progress.completed) ? progress.completed : 0;
  const total = Number.isFinite(progress.total) ? progress.total : 0;
  const percentage = clampPercent(progress.percentage);

  return (
    <aside className="w-72 shrink-0 overflow-hidden border-r border-gray-200 bg-gray-50">
      <div className="h-full overflow-y-auto p-3">
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white px-4 py-3.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                  Sections
                </p>
                <p className="mt-1 text-xs text-gray-600">
                  <span className="font-semibold text-gray-800">{completed}</span>
                  <span className="text-gray-400"> / </span>
                  <span>{total}</span>
                  <span className="text-gray-400"> {progressScopeLabel}</span>
                </p>
              </div>
              <span className="shrink-0 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-bold tabular-nums text-primary-700">
                {percentage.toFixed(0)}%
              </span>
            </div>
            <div
              className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-200"
              role="progressbar"
              aria-valuenow={percentage}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${completed} of ${total} questions ${progressScopeLabel}`}
            >
              <div
                className="h-full rounded-full bg-primary-500 transition-all duration-300 ease-out"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
          <nav aria-label="RFP sections" className="max-h-[calc(100vh-380px)] overflow-y-auto">
            {sections.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-gray-500">
                No sections with questions assigned to you.
              </p>
            ) : null}
            {sections.map((section) => {
              const isActive = section.name === activeSectionName;
              const sectionPct =
                section.total > 0 ? Math.round((section.completed / section.total) * 100) : 0;
              return (
                <button
                  key={section.name}
                  type="button"
                  onClick={() => onSectionChange(section.name)}
                  className={`block w-full border-b border-gray-100 px-4 py-3 text-left transition-colors last:border-b-0 ${
                    isActive
                      ? "border-l-[3px] border-l-primary-500 bg-primary-50/60 pl-[13px]"
                      : "border-l-[3px] border-l-transparent hover:bg-gray-50"
                  }`}
                >
                  <span
                    className={`block text-sm font-bold leading-snug ${
                      isActive ? "text-primary-700" : "text-gray-800"
                    }`}
                  >
                    {section.name}
                  </span>
                  <span className="mt-0.5 block text-[11px] tabular-nums text-gray-500">
                    {section.completed}/{section.total}
                    {section.total > 0 ? ` · ${sectionPct}%` : ""}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    </aside>
  );
};
